// src/components/AgentList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { agentService } from '../services/agentService';
import { 
  CpuChipIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import './AgentList.css';

const AgentList = ({ onEditAgent, onAgentClick, refreshTrigger, showAsLinks = false }) => {
  const [agents, setAgents] = useState([]);
  const [datasources, setDatasources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(null);

  // ✅ OPTIMIZED: Memoized datasource lookup function
  const datasourceMap = useMemo(() => {
    return datasources.reduce((map, ds) => {
      map[ds.id] = ds.name;
      return map;
    }, {});
  }, [datasources]);

  const getDatasourceName = useCallback((datasourceId) => {
    return datasourceMap[datasourceId] || `ID: ${datasourceId}`;
  }, [datasourceMap]);

  // ✅ OPTIMIZED: Single API call to get agents with datasource info
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // ✅ OPTIMIZED: Use new combined endpoint
      const agentsWithDatasources = await agentService.getAgentsWithDatasources();
      
      if (agentsWithDatasources && agentsWithDatasources.agents) {
        setAgents(agentsWithDatasources.agents);
        
        // ✅ OPTIMIZED: Extract unique datasources from agent data (no separate API call needed)
        const uniqueDatasources = agentsWithDatasources.agents.reduce((acc, agent) => {
          if (agent.datasource_id && agent.datasource_name && !acc.find(ds => ds.id === agent.datasource_id)) {
            acc.push({
              id: agent.datasource_id,
              name: agent.datasource_name,
              db_type: agent.datasource_type
            });
          }
          return acc;
        }, []);
        
        setDatasources(uniqueDatasources);
      } else {
        setAgents([]);
        setDatasources([]);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data: ' + error.message);
      setAgents([]);
      setDatasources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ OPTIMIZED: Only reload when refreshTrigger actually changes
  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  const handleDeleteAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to delete agent "${agent.agent_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleteLoading(agent.agent_id);
      await agentService.deleteAgent(agent.agent_id);
      setAgents(prev => prev.filter(a => a.agent_id !== agent.agent_id));
    } catch (error) {
      setError('Failed to delete agent: ' + error.message);
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleAgentNameClick = (agent) => {
    if (onAgentClick) {
      onAgentClick(agent);
    } else if (onEditAgent) {
      onEditAgent(agent);
    }
  };

  if (loading) {
    return (
      <div className="agent-list-loading">
        <div className="spinner"></div>
        <span>Loading agents...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-list-error">
        <ExclamationTriangleIcon className="w-5 h-5" />
        <span>{error}</span>
        <button onClick={loadData} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="no-agents">
        <div className="flex justify-center mb-4">
          <CpuChipIcon className="w-16 h-16 text-gray-400" />
        </div>
        <h3>No Agents Created Yet</h3>
        <p>Create your first agent to start querying your databases with AI assistance.</p>
      </div>
    );
  }

  return (
    <div className="agent-list">
      <div className="agent-list-header">
        <h3>Your Agents ({agents.length})</h3>
      </div>
      
      {/* Debug info - remove this later */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{fontSize: '12px', color: '#666', marginBottom: '10px'}}>
          Debug: {agents.length} agents loaded
        </div>
      )}
      
      {/* Tabular View */}
      <div className="agent-table-container">
        <table className="agent-table">
          <thead>
            <tr>
              <th className="table-header">#</th>
              <th className="table-header">Agent Name</th>
              <th className="table-header">Datasource</th>
              <th className="table-header">Created</th>
              <th className="table-header">Actions</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent, index) => (
              <tr key={agent.agent_id} className="agent-row">
                <td className="table-cell serial-number">{index + 1}</td>
                <td className="table-cell agent-name-cell">
                  {showAsLinks ? (
                    <button 
                      className="agent-name-link"
                      onClick={() => handleAgentNameClick(agent)}
                      title="Click to use this agent"
                    >
                      {agent.agent_name}
                    </button>
                  ) : (
                    <span className="agent-name">{agent.agent_name}</span>
                  )}
                </td>
                <td className="table-cell datasource-cell">
                  {agent.datasource_name || getDatasourceName(agent.datasource_id)}
                </td>
                <td className="table-cell date-cell">
                  {agent.created_at ? new Date(agent.created_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="table-cell actions-cell">
                  <div className="action-buttons">
                    {showAsLinks ? (
                      <button
                        className="btn-chat"
                        onClick={() => handleAgentNameClick(agent)}
                        title="Use this agent for queries"
                      >
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        className="btn-chat"
                        onClick={() => onEditAgent && onEditAgent(agent)}
                        title="Use this agent for queries"
                      >
                        <ChatBubbleLeftRightIcon className="w-4 h-4" />
                      </button>
                    )}
                    
                    <button 
                      className="btn-delete"
                      onClick={() => handleDeleteAgent(agent)}
                      disabled={deleteLoading === agent.agent_id}
                      title="Delete agent"
                    >
                      {deleteLoading === agent.agent_id ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : (
                        <TrashIcon className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AgentList;
